import React, { FunctionComponent } from 'react'

import { get } from 'lodash-es'

import {
  AssignmentResponse,
  Contentmetadata,
} from '@lyrahealth-inc/shared-app-logic/src/models/assignments/Assignments'
import { UiMetadata } from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'

import { DataTable } from '../dataTable/DataTable'

interface ReportTableProps {
  metaData?: Contentmetadata
  data: AssignmentResponse[]
  rowClickHandler: (rowInfo: AssignmentResponse) => void
  UIMetadata?: UiMetadata
  stickyColumn?: string
}

type Column = {
  accessor: string
  Cell?: (props: any) => Element
  Header?: string
  name?: string
  width?: number
  minWidth?: number
  moveToEnd?: boolean
}

export const ReportTable: FunctionComponent<ReportTableProps> = ({
  data,
  rowClickHandler,
  UIMetadata,
  metaData,
  stickyColumn,
}) => {
  const columns: {
    id: string | undefined
    accessorKey: string
    header?: string
    width?: number
    cell?: (props: any) => Element
  }[] = []
  const schemaProperties: { [field: string]: any } = get(metaData, 'schema.properties', [])
  const fixedColumns = get(UIMetadata, 'report.fixedColumns', [])
  const customColumns = get(UIMetadata, 'report.customColumns', [])
  const fields = Object.keys(get(data?.[0], 'response', []))

  customColumns.forEach((column: Column) => {
    const customColumnName = get(column, 'name')
    const customFieldHeader = customColumnName
      ? get(schemaProperties[customColumnName], 'shortTitle') || get(schemaProperties[customColumnName], 'title')
      : null

    if (customColumnName && fields.includes(customColumnName)) {
      columns.push({
        id: column.name,
        accessorKey: column.accessor,
        header: customFieldHeader || column.Header,
        cell: column.Cell,
        ...(column.width && { width: column.width }),
      })
    }
  })

  for (const field of fields) {
    const Header = get(schemaProperties[field], 'shortTitle') || get(schemaProperties[field], 'title')
    const accessor = 'response.' + get(schemaProperties[field], 'name')
    const width = 'response.' + get(schemaProperties[field], 'width')

    // Prevent potential duplicates of custom columns
    if (Header && !customColumns.some((column: Column) => column.accessor.includes(accessor))) {
      columns.push({
        id: field,
        accessorKey: accessor,
        header: Header,
        ...(width && { width: parseInt(width) }),
      })
    }
  }

  fixedColumns.forEach((column: Column) =>
    column.moveToEnd
      ? columns.push({
          id: column.name,
          accessorKey: column.accessor,
          header: column.Header,
          cell: column.Cell,
          ...(column.width && { width: column.width }),
        })
      : columns.unshift({
          id: column.name,
          accessorKey: column.accessor,
          header: column.Header,
          cell: column.Cell,
          ...(column.width && { width: column.width }),
        }),
  )

  return (
    <DataTable
      data={data}
      columns={columns}
      onRowClick={(row) => rowClickHandler(row.original)}
      rowHeight={48}
      stickyColumn={stickyColumn}
    />
  )
}
